import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../../components/ui/Button.js';
import { api } from '../../../services/api.js';
import { LegalDocumentsCard } from '../LegalDocumentsCard.js';
import { LastChanged } from '../parts/LastChanged.js';
import { SettingCard, StatusChip } from '../parts/parts.js';
import type { LatestChanges } from '../settingsApi.js';
import { formatMyt } from '../settingsFormat.js';

const DOCUMENTS = [
  { kind: 'terms_of_service', label: 'Terms of service' },
  { kind: 'privacy_policy', label: 'Privacy policy' },
] as const;

interface PublishedDocument {
  version: string;
  published_at: string;
}

function DocumentRow({
  kind,
  label,
  latest,
}: {
  kind: (typeof DOCUMENTS)[number]['kind'];
  label: string;
  latest?: LatestChanges;
}) {
  // Same key as the editor's read, so the two share one request.
  const read = useQuery({
    queryKey: ['legal-document', kind],
    queryFn: () => api.get<PublishedDocument>(`/legal-documents/${kind}`),
    retry: false,
  });
  return (
    <div
      data-testid={`legal-${kind}`}
      className="flex flex-col gap-1 border-t border-hairline-soft pt-3 first:border-t-0 first:pt-0"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-ink">{label}</span>
        {read.data ? (
          <StatusChip tone="on">{`Version ${read.data.version}`}</StatusChip>
        ) : (
          <StatusChip tone="off">{read.isLoading ? 'Loading…' : 'Not published'}</StatusChip>
        )}
      </div>
      {read.data && (
        <span className="text-xs text-steel">Published {formatMyt(read.data.published_at)}</span>
      )}
      <LastChanged setting={kind} latest={latest} />
    </div>
  );
}

/** 356 (FR-024): a summary per legal document in front of the unchanged editor. */
export function LegalSummaryCard({ latest }: { latest?: LatestChanges }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      <SettingCard
        testId="legal-summary-card"
        title="Terms and privacy"
        dirty={false}
        description={
          <p>
            What customers agree to. Publishing a new version replaces the current one for every
            customer, and is recorded in History.
          </p>
        }
        footer={
          <Button type="button" variant="secondary" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Close the editor' : 'Edit and publish'}
          </Button>
        }
      >
        {DOCUMENTS.map((d) => (
          <DocumentRow key={d.kind} kind={d.kind} label={d.label} latest={latest} />
        ))}
      </SettingCard>
      {editing && <LegalDocumentsCard />}
    </div>
  );
}
